/**
 * One schedule-table date cell (spec 05-TBL §5.1). Its three states derive purely from
 * `dates[field].source` + `locked`:
 *   - reported (locked): grey, non-editable — a click explains "Reported to regulator - locked";
 *   - override: blue text with a calendar edit affordance;
 *   - modeled (scheduled / on-file, unlocked): default text with a calendar edit affordance.
 * Unscheduled milestones (null value) render an em dash and are not editable — the same
 * rule as the chart (their remedy is configuration). Editing opens the house DatePicker
 * and commits an ISO day through the caller, which clamps it to the lifecycle-order
 * neighbor bounds (the shared `clampIsoToNeighbors` rule the chart drag also applies) and
 * routes it to the SHARED edit layer (`edits.applyDrag`) — the identical working-log
 * transform + order/locked rules, so a table edit and a chart drag stay in lock-step.
 */

import { useEffect, useState } from 'react';
import moment from 'moment';
import { uniqueId } from 'lodash-es';
import { Toggletip, ToggletipButton, ToggletipContent } from '@carbon/react';
import { Calendar } from '@carbon/react/icons';
import { Date as DateDisplay } from 'components/date';
import { DatePicker } from 'components/date-picker/date-picker';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { parseDateString, parseTypedDate } from 'utils/dates';
import { EMPTY_CELL } from './table-model';
import type { MergedDate } from './types';
import styles from './schedule-date-cell.module.scss';

/** The message a reported (locked) cell surfaces on click. */
export const REPORTED_LOCK_MESSAGE = 'Reported to regulator - locked';

/**
 * flatpickr's parse for the cell's text — a whole valid m/d/yyyy date, or nothing. Carbon's
 * default parse coerces rather than refuses (`13/45/2028` became 01/01/2028). Enter, blur
 * and a click outside all run the text through this parse; the keyboard exits that skip it
 * are judged in `onClose`. Refusing makes flatpickr clear the text, so the stored date
 * stands.
 *
 * @param {string} text - The field's text, typed or the picker's own m/d/Y value.
 * @return {Date | false} The day at local midnight, or false when it is not a whole date.
 */
const parseCellDate = (text: string): Date | false =>
  parseDateString(parseTypedDate(text)) ?? false;

export interface ScheduleDateCellProps {
  /** The row's EFFECTIVE merged date for this milestone (base overlaid with the working log). */
  date: MergedDate;
  /** The milestone label (drives the date-picker + edit affordance labels). */
  label: string;
  /**
   * Commits a new ISO day (routed to the shared edit layer by the caller). One edit may
   * commit the same day more than once — a keyboard exit commits on close and again on the
   * blur that follows — so it must be idempotent, as the edit layer's upsert is.
   */
  onCommit: (isoDay: string) => void;
}

/**
 * Renders a single date cell in the schedule table.
 *
 * @param {ScheduleDateCellProps} props - The merged date, the milestone label, and the commit handler.
 * @return {JSX.Element} The date cell.
 */
export function ScheduleDateCell({
  date,
  label,
  onCommit,
}: ScheduleDateCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputId] = useState(() => uniqueId('schedule-date-'));

  // Focusing the input opens the calendar (Carbon opens flatpickr on focus).
  useEffect(() => {
    if (isEditing) {
      document.getElementById(inputId)?.focus();
    }
  }, [isEditing, inputId]);

  // Reported (locked) — grey and non-editable; a click explains the lock.
  if (date.locked) {
    return (
      <Toggletip align="top">
        <ToggletipButton
          label={REPORTED_LOCK_MESSAGE}
          className={styles.reportedButton}
        >
          <span className={styles.reported}>
            {date.value ? (
              <DateDisplay
                date={date.value}
                format="MM/DD/YYYY"
                fallback={EMPTY_CELL}
              />
            ) : (
              EMPTY_CELL
            )}
          </span>
        </ToggletipButton>
        <ToggletipContent>{REPORTED_LOCK_MESSAGE}</ToggletipContent>
      </Toggletip>
    );
  }

  // Unscheduled — no milestone to edit (same rule as the chart).
  if (date.value === null) {
    return <span className={styles.empty}>{EMPTY_CELL}</span>;
  }

  if (isEditing) {
    return (
      // No `appendTo`: flatpickr then hangs the calendar off `document.body`, outside the
      // dialog's scroll clip, and positions it itself — above the input near the bottom
      // edge, right-aligned near the right one. Any `appendTo` (the body included) loads
      // Carbon's appendTo plugin, which pins the calendar's left edge to the input's, so
      // the rightmost column's calendar ran off-screen (INS-13660).
      <DatePicker
        id={inputId}
        labelText={label}
        size="sm"
        value={date.value}
        parseDate={parseCellDate}
        onChange={(dates) => {
          const picked = dates?.[0];
          if (picked && moment(picked).isValid()) {
            onCommit(moment(picked).format('YYYY-MM-DD'));
          }
        }}
        onClose={(_dates, _dateStr, picker) => {
          // The field's text when the picker closes is the date the user meant. A pick or
          // Enter has already been written into it. A click outside re-parses it (through
          // `parseCellDate`) but emits no change, which is the drop INS-13660 reported.
          // Leaving by keyboard (Shift+Tab, or Tab out of the calendar) closes before any
          // parse; the blur that follows may commit the same day again. Text matching the
          // date the cell already holds commits nothing — committing it would turn a
          // modeled date into an override.
          const isoDay = parseTypedDate(picker.input.value);
          if (isoDay && isoDay !== date.value) {
            onCommit(isoDay);
          }
          setIsEditing(false);
        }}
      />
    );
  }

  const isOverride = date.source === 'override';
  return (
    <div className={styles.cell}>
      <span className={isOverride ? styles.override : styles.value}>
        <DateDisplay
          date={date.value}
          format="MM/DD/YYYY"
          fallback={EMPTY_CELL}
        />
      </span>
      <FloatingTooltip content={`Edit ${label}`} placement="top">
        <button
          type="button"
          className={styles.editButton}
          aria-label={`Edit ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
        >
          <Calendar />
        </button>
      </FloatingTooltip>
    </div>
  );
}
